import { Icon } from '@aqarmap/aqarmap-ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocationType } from '~addListing/types';
import { useGetLocationsMutation } from '~/redux/api/userApiSlice';
import LocationsSkeleton from '~addListing/pages/InitializeStep/Location/LocationsSkeleton';
import { FieldErrors, UseFormClearErrors } from 'react-hook-form';
import LocationsItems from '~addListing/pages/InitializeStep/Location/LocationsItems';
import { LocationSearchType } from '.';

const SubLocationSearch = ({
  parentLocation,
  location,
  handleSelectLocation,
  searchMode = 'sameLevel',
  errors,
  clearErrors,
  allowSelectAllLocations,
  hideSuggestLocation,
}: {
  parentLocation: LocationType | null;
  location: LocationType | null;
  handleSelectLocation: (location: LocationType) => void;
  searchMode?: 'sameLevel' | 'children';
  errors: FieldErrors<LocationSearchType>;
  clearErrors: UseFormClearErrors<LocationSearchType>;
  allowSelectAllLocations?: boolean;
  hideSuggestLocation?: boolean;
}) => {
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation('app');
  const [showContent, setShowContent] = useState(false);
  const [isError, setIsError] = useState(false);
  const [subLocations, setSubLocations] = useState<LocationType[]>([]);
  const [selectedSubLocation, setSelectedSubLocation] =
    useState<LocationType | null>(null);
  const [keywordSearch, setKeywordSearch] = useState('');
  const [getLocations, { isLoading }] = useGetLocationsMutation();

  const getSearchQuery = () => {
    const searchQuery = {
      keywordSearch,
      limit: 100,
      locationParent:
        searchMode === 'sameLevel' ? location?.parent.id : parentLocation?.id,
    };
    const filteredQuery = Object.fromEntries(
      Object.entries(searchQuery).filter(
        ([key, value]) =>
          !(key === 'locationParent' && value === 0) &&
          !(key === 'keywordSearch' && value === ''),
      ),
    );

    const queryString = new URLSearchParams(
      filteredQuery as Record<string, string>,
    ).toString();
    return queryString;
  };

  const handleSelectSubLocation = (location: LocationType) => {
    setSelectedSubLocation(location);
    handleSelectLocation(location);
    setKeywordSearch(location.title);
    setShowContent(false);
  };

  const handleFetchSubLocations = async () => {
    const res = await getLocations(getSearchQuery());
    if ('data' in res) setSubLocations(res.data);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownMenuRef.current &&
        !dropdownMenuRef.current.contains(e.target as Node)
      ) {
        setShowContent(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setSelectedSubLocation(parentLocation);
  }, []);

  useEffect(() => {
    handleFetchSubLocations();
  }, [keywordSearch]);

  useEffect(() => {
    setIsError(false);
  }, []);

  useEffect(() => {
    if (errors.location) setIsError(true);
    else setIsError(false);
  }, [errors]);

  return (
    <div ref={dropdownMenuRef} className="relative select-none">
      <div
        onClick={() => {
          setShowContent(!showContent);
          setIsError(false);
          clearErrors('location');
        }}
        className={`w-full h-[40px] flex items-center border ${isError ? 'border-red_dark' : 'border-gray'}  ${showContent ? 'rounded-t-[12px]' : 'rounded-[12px]'} justify-between cursor-pointer border-b  px-1.5x py-x`}
      >
        <div className="flex gap-1.5x">
          <Icon name="location" size="24px" className="!text-gray__dark_1" />
          <p
            className={`text-body_2 ${searchMode === 'sameLevel' ? '!text-gray__dark_2' : '!text-gray__dark_1'} `}
          >
            {searchMode === 'sameLevel'
              ? selectedSubLocation?.title
              : t('chooseLocation')}
          </p>
        </div>
        <Icon
          name="chevron-down"
          className={`${showContent ? '!rotate-180' : ''}`}
        />
      </div>
      {showContent && (
        <div
          className={`content absolute top-full left-0 w-full ${hideSuggestLocation ? 'max-h-[310px]' : 'max-h-[216px]'} border ${isError ? 'border-red_dark' : 'border-gray'} border-t-0 bg-white rounded-b-[12px] overflow-hidden z-10`}
        >
          <div className="flex gap-x items-center border border-gray rounded-[12px] px-1.5x py-x text-gray__dark_1 mt-1.5x mb-0.5x mx-1.5x">
            <Icon name="search" size="24px" />
            <input
              type="text"
              className="flex-1 placeholder:text-gray__dark_1 text-gray__dark_2"
              placeholder={t('search')}
              onChange={(e) => setKeywordSearch(e.target.value)}
            />
          </div>
          <div
            className={`pb-1.5x ${hideSuggestLocation ? 'max-h-[360px]' : 'max-h-[260px]'}`}
          >
            {isLoading ? (
              <LocationsSkeleton />
            ) : subLocations.length === 0 ? (
              <p className="px-1.5x pb-x pt-0.5x text-body_2 text-gray__dark_2">
                {t('noResults')}
              </p>
            ) : (
              <LocationsItems
                handleSelectLocation={handleSelectSubLocation}
                locations={subLocations}
                allowSelectAllLocations={allowSelectAllLocations}
                hideSuggestLocation={hideSuggestLocation}
              />
            )}
          </div>
        </div>
      )}
      {isError && (
        <p className="text-red_dark text-body_2 !mb-0.5x mt-1.5x">
          {t('requiredLocation')}
        </p>
      )}
    </div>
  );
};

export default SubLocationSearch;
